<template>
  <div v-if="anchors.length" ref="tabsContainer" class="scrollbar-none">
    <RevTabs
      class="isolate"
      :label="i18n(translations.tabsLabel)"
      variant="flexible"
    >
      <RevTabItem
        v-for="(tab, index) in anchors"
        :key="tab.id"
        :active="activeAnchor === tab.id"
        :label="tab.label"
        @click="onTabClick(tab, index)"
      />
    </RevTabs>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'

import type { Anchor } from '../../../models/productComparison'

import translations from './Tabs.translations'

const { anchors } = defineProps<{
  anchors: Anchor[]
}>()

const emit = defineEmits<{
  'tab-click': [payload: { tabId: string; tabLabel: string; position: number }]
}>()

const i18n = useI18n()
const tabsContainer = useTemplateRef<HTMLElement>('tabsContainer')
const activeAnchor = ref<string | null>(null)

// Snap (not smooth) on the first activation after mount, so the sticky tab
// bar reappears already aligned instead of scrolling to the active tab.
let hasActivated = false
watch(activeAnchor, (id) => {
  if (!id) return
  const index = anchors.findIndex((t) => t.id === id)
  if (index === -1) return

  const nav = tabsContainer.value?.querySelector('nav')
  const item = tabsContainer.value?.querySelector<HTMLElement>(
    `[role="tablist"] > :nth-child(${index + 1})`,
  )
  if (!nav || !item) return

  const { offsetLeft, offsetWidth } = item
  const { scrollLeft, offsetWidth: navWidth } = nav
  const padding = 16
  const behavior = hasActivated ? 'smooth' : 'auto'
  hasActivated = true

  if (offsetLeft < scrollLeft) {
    nav.scrollTo({ left: offsetLeft - padding, behavior })
  } else if (offsetLeft + offsetWidth > scrollLeft + navWidth) {
    nav.scrollTo({
      left: offsetLeft + offsetWidth - navWidth + padding,
      behavior,
    })
  }
})

function scrollToAnchor(id: string) {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function onTabClick(tab: Anchor, index: number) {
  emit('tab-click', {
    tabId: tab.id,
    tabLabel: tab.label,
    position: index + 1,
  })
  scrollToAnchor(tab.id)
}

let anchorObserver: IntersectionObserver | null = null

function observeAnchors() {
  anchorObserver?.disconnect()
  if (!anchors.length) return

  // Matches the `scroll-mt-*` on the headings (see TableContent.vue);
  // `+ 4` absorbs sub-pixel landings of scrollIntoView at end-of-scroll.
  const stickyHeight = window.matchMedia('(min-width: 768px)').matches
    ? 202
    : 196
  const activationLine = stickyHeight + 4

  // Active = the last heading whose top has crossed `activationLine`.
  function updateActive() {
    let active = anchors[0]?.id ?? null
    for (const { id } of anchors) {
      const el = document.getElementById(id)
      if (!el) continue
      if (el.getBoundingClientRect().top <= activationLine) {
        active = id
      } else {
        break
      }
    }
    activeAnchor.value = active
  }

  anchorObserver = new IntersectionObserver(updateActive, {
    rootMargin: `-${activationLine}px 0px 0px 0px`,
    threshold: 1.0,
  })

  for (const { id } of anchors) {
    const el = document.getElementById(id)
    if (el) anchorObserver.observe(el)
  }

  updateActive()
}

onMounted(observeAnchors)
onBeforeUnmount(() => anchorObserver?.disconnect())
watch(() => anchors, observeAnchors)
</script>

<style scoped>
/* RevTabs flexible variant adds a bottom margin below its border — hide the scrollbar
   without losing scroll functionality when this component is used in a sticky header. */
.scrollbar-none :deep(nav) {
  scrollbar-width: none;
}
.scrollbar-none :deep(nav::-webkit-scrollbar) {
  display: none;
}
</style>
